import { useEffect, useMemo, useRef } from 'react';
import type { ScriptSpan } from '@/lib/mlReviewUnits';

type ScriptHighlightPaneProps = {
  text: string;
  accepted?: ScriptSpan[];
  current: ScriptSpan | ScriptSpan[] | null;
  next: ScriptSpan | ScriptSpan[] | null;
  draft?: ScriptSpan | null;
  captureSelection?: boolean;
  statusLabel?: string;
  onSelectSpan?: (span: ScriptSpan | null) => void;
};

type Seg = { start: number; end: number; kind: 'accepted' | 'current' | 'next' | 'draft' | 'plain' };

function asSpans(value: ScriptSpan | ScriptSpan[] | null | undefined): ScriptSpan[] {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
}

function mergeSpans(
  text: string,
  accepted: ScriptSpan[],
  current: ScriptSpan[],
  next: ScriptSpan[],
  draft: ScriptSpan | null,
): Seg[] {
  const marks: { pos: number; kind: Seg['kind']; open: boolean }[] = [];
  const add = (span: ScriptSpan | null, kind: Seg['kind']) => {
    if (!span) return;
    const start = Math.max(0, Math.min(text.length, span.start));
    const end = Math.max(start, Math.min(text.length, span.end));
    if (end <= start) return;
    marks.push({ pos: start, kind, open: true });
    marks.push({ pos: end, kind, open: false });
  };
  for (const span of accepted) add(span, 'accepted');
  for (const span of current) add(span, 'current');
  for (const span of next) add(span, 'next');
  add(draft, 'draft');
  marks.sort((a, b) => a.pos - b.pos || Number(a.open) - Number(b.open));
  const segs: Seg[] = [];
  let cursor = 0;
  const active: Seg['kind'][] = [];
  const rank = (k: Seg['kind']) =>
    k === 'draft' ? 4 : k === 'current' ? 3 : k === 'next' ? 2 : k === 'accepted' ? 1 : 0;
  const top = (): Seg['kind'] =>
    active.reduce((best, k) => (rank(k) > rank(best) ? k : best), 'plain' as Seg['kind']);
  for (const mark of marks) {
    if (mark.pos > cursor) {
      segs.push({ start: cursor, end: mark.pos, kind: top() });
      cursor = mark.pos;
    }
    if (mark.open) active.push(mark.kind);
    else {
      const idx = active.lastIndexOf(mark.kind);
      if (idx >= 0) active.splice(idx, 1);
    }
  }
  if (cursor < text.length) segs.push({ start: cursor, end: text.length, kind: 'plain' });
  return segs;
}

const KIND_CLASS: Record<Seg['kind'], string> = {
  plain: 'text-white/70',
  accepted: 'bg-white/15 text-white/55',
  current: 'bg-amber-400/30 text-white ring-1 ring-amber-300/70',
  next: 'bg-sky-400/15 text-white/85 outline outline-1 outline-sky-300/50',
  draft: 'bg-emerald-400/35 text-white ring-1 ring-emerald-300/80',
};

function scriptOffset(node: Node, offset: number, root: HTMLElement): number | null {
  let el: Node | null = node.nodeType === Node.TEXT_NODE ? node.parentElement : node;
  while (el && el !== root) {
    if (el instanceof HTMLElement && el.dataset.start != null) {
      return Number(el.dataset.start) + offset;
    }
    el = el.parentElement;
  }
  return null;
}

function selectionToSpan(root: HTMLElement): ScriptSpan | null {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return null;
  const range = sel.getRangeAt(0);
  if (!root.contains(range.commonAncestorContainer)) return null;
  const start = scriptOffset(range.startContainer, range.startOffset, root);
  const end = scriptOffset(range.endContainer, range.endOffset, root);
  if (start == null || end == null || end <= start) return null;
  return { start, end };
}

export default function ScriptHighlightPane({
  text,
  accepted = [],
  current,
  next,
  draft = null,
  captureSelection = false,
  statusLabel,
  onSelectSpan,
}: ScriptHighlightPaneProps) {
  const currentRef = useRef<HTMLElement | null>(null);
  const rootRef = useRef<HTMLPreElement | null>(null);
  const segs = useMemo(
    () => mergeSpans(text, accepted, asSpans(current), asSpans(next), draft),
    [text, accepted, current, next, draft],
  );
  const scrollStart = asSpans(current)[0]?.start;
  const scrollEnd = asSpans(current)[0]?.end;

  useEffect(() => {
    currentRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [scrollStart, scrollEnd]);

  const emitSelection = () => {
    if (!captureSelection || !onSelectSpan || !rootRef.current) return;
    onSelectSpan(selectionToSpan(rootRef.current));
  };

  return (
    <div className="h-full min-h-0 flex flex-col overflow-hidden rounded-sm border border-[#2a2a2a] bg-[#0a0a0a]">
      <div className="sticky top-0 z-20 shrink-0 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-[#2a2a2a] bg-[#0a0a0a]/95 px-4 py-2.5 backdrop-blur-sm">
        {statusLabel ? (
          <p className="ni-label truncate min-w-0">{statusLabel}</p>
        ) : (
          <span />
        )}
        <div className="flex flex-wrap gap-3 text-[10px] font-mono uppercase tracking-wider text-white/40">
          <span><span className="inline-block w-2 h-2 bg-white/20 mr-1 align-middle" />Accepted</span>
          <span><span className="inline-block w-2 h-2 bg-amber-400/60 mr-1 align-middle" />Current</span>
          <span><span className="inline-block w-2 h-2 bg-sky-400/40 mr-1 align-middle" />Next</span>
          {captureSelection && (
            <span><span className="inline-block w-2 h-2 bg-emerald-400/70 mr-1 align-middle" />New label</span>
          )}
        </div>
      </div>
      <div className="flex-1 min-h-0 overflow-auto ni-scroll p-4">
        {captureSelection && (
          <p className="mb-3 text-xs font-sans text-emerald-200/80">
            Select the missing span in the screenplay to attach it to the new label.
          </p>
        )}
        <pre
          ref={rootRef}
          className="whitespace-pre-wrap break-words font-mono text-[12px] leading-5"
          onMouseUp={emitSelection}
          onKeyUp={emitSelection}
        >
          {segs.map((seg, i) => {
            const chunk = text.slice(seg.start, seg.end);
            if (seg.kind === 'plain') {
              return (
                <span key={i} data-start={seg.start} className={KIND_CLASS.plain}>
                  {chunk}
                </span>
              );
            }
            return (
              <mark
                key={i}
                ref={seg.kind === 'current' ? currentRef : undefined}
                data-start={seg.start}
                className={KIND_CLASS[seg.kind]}
              >
                {chunk}
              </mark>
            );
          })}
        </pre>
      </div>
    </div>
  );
}
